import http, { ResponseInfo } from '../utils/http';
import storage from '../utils/storage';
import Notification from '../view-model/users/Notification';
import NotificationSummary from '../view-model/users/NotificationSummary';

const now = Date.now();

type ReadNotification = { hash: string, user: number, date: Date };

let readNotifications: ReadNotification[] | null = null;

const isRead = (hash: string, duration?: number) => {
    if (readNotifications === null)
        readNotifications = storage.getJSON<ReadNotification[]>('brax.user.notifications') || [];

    const notification = readNotifications.find(notification => notification.hash === hash);

    if (!notification || duration === 0)
        return false;

    if (!duration || now - new Date(notification.date).getTime() <= duration * 1000)
        return true;

    readNotifications = readNotifications.filter(notification => notification.hash !== hash);

    return false;
};

type SubscriptionCallback = (hash: string, data: { read: true }) => void;

const subscriptions: (SubscriptionCallback | null)[] = [];

const subscribe = (callback: SubscriptionCallback) => {
    subscriptions.push(callback);

    return () => unsubscribe(subscriptions.length - 1);
};

const unsubscribe = (index: number) => {
    subscriptions[index] = null;
};

const markAsRead = (user: number, hash: string, duration?: number) => {
    if (isRead(hash, duration))
        return;

    readNotifications?.push({ user, hash, date: new Date() });

    storage.setJSON('brax.user.notifications', readNotifications);

    for (const subscription of subscriptions)
        subscription?.(hash, { read: true });
};

const getNotifications = async (user: number): Promise<ResponseInfo<NotificationSummary[]>> => {
    const response = await http.get<Omit<NotificationSummary, 'read'>[]>(`/v2.1/users/${user}/notifications`);

    return {
        ...response,
        result: checkIsRead(response.result)
    };
};

const checkIsRead = (notifications: (Omit<NotificationSummary, 'read'> & Partial<Pick<NotificationSummary, 'read'>>)[]) =>
    notifications.map(notification => ({ ...notification, read: isRead(notification.hash, notification.duration) }));

const getNotification = async (user: number, id: number): Promise<ResponseInfo<Notification>> => {
    const response = await http.get<Omit<Notification, 'id'>>(`/v2.1/users/${user}/notifications/${id}`);

    return {
        ...response,
        result: { id, ...response.result }
    };
};

const notificationsService = { subscribe, checkIsRead, getNotifications, getNotification, markAsRead };

export default notificationsService;
